import { coverMediaKey, withCoverPhoto, type InspectionCoverPhoto, type V2Inspection } from '../domain/inspection';
import { resizeImage } from '../native/imageTools';
import { getMediaAdapter } from '../native/mediaAdapter';
import type { AlbumSaveResult, CapturedFile } from '../native/mediaContract';
import { deleteMediaWithRecords, putMediaWithRecords } from '../persistence/v2Database';
import { assertInspectionUnlocked, inspectionStore, upsertInspectionLocal } from './inspectionStore';
import { releaseMediaUrls, requestMediaUrl, useMediaUrl } from './mediaUrls';

// The report cover photo lane (Owner device feedback 2026-09-14: 「新建验屋都需要有一个拍照入口用作报告
// 封面」). ONE photo per inspection — taken or chosen on the New Inspection form (held in memory until
// Create) or from the inspection card later — stored in the media store under coverMediaKey(id) and
// described on the inspection record (V2Inspection.coverPhoto). It is shown on the inspection card, as the
// thumbnail of the Reports list and printed on the report cover. It is NOT evidence: it never enters the
// photo pool, is never linked to a row or a finding and is never sent to the AI.
//
// The photo contract (src/native/mediaContract.ts) holds here too: a camera capture has its system-album
// save requested the moment the capture succeeds, before anything else; a library pick is never re-saved;
// replacing or removing the cover touches this database only.
export type CoverLane = 'camera' | 'library';

// A photo picked for an inspection that does not exist yet (the New Inspection form): the file, the
// album outcome already requested for it, and an object URL for the preview.
export interface PendingCover {
  file: CapturedFile;
  album: AlbumSaveResult | null;
  previewUrl: string | null;
}

export type CoverPickOutcome = { kind: 'cancelled' } | { kind: 'picked'; pending: PendingCover } | { kind: 'error'; message: string };
export type CoverSaveOutcome = { kind: 'cancelled' } | { kind: 'saved'; inspection: V2Inspection } | { kind: 'error'; message: string };

// Print size for the report cover (a 3:2 photo across the page) and the card / list thumbnail.
const FULL_EDGE = 1600;
const THUMB_EDGE = 480;

function messageOf(raw: unknown): string {
  return raw instanceof Error ? raw.message : String(raw);
}

export function previewUrlFor(file: CapturedFile): string | null {
  try {
    return typeof URL !== 'undefined' && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(file.blob) : null;
  } catch {
    return null;
  }
}

export async function pickCoverFileAction(lane: CoverLane): Promise<CoverPickOutcome> {
  const adapter = getMediaAdapter();
  try {
    if (lane === 'camera') {
      const file = await adapter.captureFromCamera();
      if (!file) return { kind: 'cancelled' };
      // ALBUM FIRST (OC §6.1): requested before the preview, the resize or the store.
      const album = await adapter.saveToSystemAlbum(file).catch((raw): AlbumSaveResult => ({ state: 'failed', detail: `相册保存失败：${messageOf(raw)}` }));
      return { kind: 'picked', pending: { file, album, previewUrl: previewUrlFor(file) } };
    }
    // ONE cover per inspection: the picker is opened for a single photo (Owner device test 2026-09-21).
    const files = await adapter.pickFromLibrary({ single: true });
    const file = files[0] ?? null;
    if (!file) return { kind: 'cancelled' };
    return { kind: 'picked', pending: { file, album: null, previewUrl: previewUrlFor(file) } };
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
}

export function releasePendingCover(pending: PendingCover): void {
  if (!pending.previewUrl) return;
  try {
    URL.revokeObjectURL(pending.previewUrl);
  } catch {
    // nothing to release
  }
}

// Stores the picked photo as THE cover of the inspection: bytes (print size + thumbnail) and the
// inspection record land in one transaction. Refused once the final report exists (the printed cover
// and the list thumbnail must stay the same photo).
export async function setCoverPhotoAction(inspectionId: string, pending: PendingCover, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  const full = await resizeImage(pending.file.blob, { maxEdge: FULL_EDGE, quality: 0.86 });
  const thumb = await resizeImage(pending.file.blob, { maxEdge: THUMB_EDGE, quality: 0.82 });
  const fullBlob = full ? full.blob : pending.file.blob;
  const cover: InspectionCoverPhoto = {
    source: pending.file.source,
    mimeType: full ? full.blob.type : pending.file.mimeType,
    byteSize: fullBlob.size,
    width: full ? full.width : null,
    height: full ? full.height : null,
    // A library pick is never re-saved to the album (provenance fail-closed): 'notRequired'.
    albumSaveState: pending.album ? pending.album.state : 'notRequired',
    albumSaveDetail: pending.album ? pending.album.detail : '',
    version: (current.coverPhoto?.version ?? 0) + 1,
    updatedAt: now
  };
  const next = withCoverPhoto(current, cover, now);
  const key = coverMediaKey(inspectionId);
  await putMediaWithRecords(
    key,
    { type: cover.mimeType, bytes: await fullBlob.arrayBuffer() },
    thumb ? { type: thumb.blob.type, bytes: await thumb.blob.arrayBuffer() } : null,
    [{ store: 'inspections', value: next }]
  );
  upsertInspectionLocal(next);
  // The key is the same for every version of the cover: drop the cached URLs and load the new bytes.
  releaseMediaUrls([key]);
  requestMediaUrl(key, 'thumbnail');
  requestMediaUrl(key, 'full');
  return next;
}

// Pick + store in one step (the inspection card).
export async function captureCoverPhotoAction(inspectionId: string, lane: CoverLane, now: number = Date.now()): Promise<CoverSaveOutcome> {
  // A locked inspection is refused BEFORE the camera opens: no photo is taken that cannot be kept.
  try {
    assertInspectionUnlocked(inspectionId);
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  }
  const picked = await pickCoverFileAction(lane);
  if (picked.kind !== 'picked') return picked;
  try {
    const next = await setCoverPhotoAction(inspectionId, picked.pending, now);
    return next ? { kind: 'saved', inspection: next } : { kind: 'error', message: `inspection ${inspectionId} not found` };
  } catch (raw) {
    return { kind: 'error', message: messageOf(raw) };
  } finally {
    releasePendingCover(picked.pending);
  }
}

// Removes the app's copy only; the system album is never involved (OC §6.1.2).
export async function removeCoverPhotoAction(inspectionId: string, now: number = Date.now()): Promise<V2Inspection | null> {
  assertInspectionUnlocked(inspectionId);
  const current = inspectionStore.get().inspections.find((i) => i.id === inspectionId);
  if (!current) return null;
  if (current.coverPhoto === null) return current;
  const next = withCoverPhoto(current, null, now);
  const key = coverMediaKey(inspectionId);
  await deleteMediaWithRecords(key, [{ store: 'inspections', value: next }]);
  upsertInspectionLocal(next);
  releaseMediaUrls([key]);
  return next;
}

export function useCoverPhotoUrl(inspection: Pick<V2Inspection, 'id' | 'coverPhoto'>, variant: 'full' | 'thumbnail'): string | null {
  return useMediaUrl(inspection.coverPhoto ? coverMediaKey(inspection.id) : '', variant);
}
